import {Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation, inject} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ApiService} from "../../services/api/api.service";
import {HandsontableGeneratorService, HOTLookup} from "../../services/handsontable-generator.service";
import {HeaderDataService} from "../../services/header_data.service";
import {uniq as _uniq} from "lodash-es";
import {HotInstance} from "../../services/hot-instance";
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {catchError, debounceTime, first, map, switchMap, tap, concatMap, finalize} from 'rxjs/operators';
import {EMPTY, firstValueFrom, forkJoin, Observable, of, Subject} from "rxjs";
import {ListResponse} from "../../services/api/response-types";
import {SeriesComponent} from "../../_models/series-component";
import {User} from "@sentry/browser";
import {WireComponent} from "../../_models/component";
import {getBaseFilter} from "../../services/api/filter_utils";
import {SeriesLight} from "../../_models/series";
import {PivotViewComponent} from "../pivot-view/pivot-view.component";
import {HistoryTableComponent} from "../history-table/history-table.component";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {UtilsBarComponent} from "../../tables/utils-bar/utils.bar.component";
import {AccountFilterComponent} from "../../components/account-filter/account-filter.component";
import {NotificationService} from "../../services/notification.service";

type PendingPasteLookup = { text: string; token: number };
type SeriesQuery = {
    query: string;
    process: (names: string[]) => void;
    exactNameOnly?: boolean;
    cachedNames?: string[];
};

/**
 * This is called 'Report configuration' on the front-end
 */
@Component({
    selector: 'series-component-sheet-view',
    imports: [
        SharedModule,
        PivotViewComponent,
        HistoryTableComponent,
        UtilsBarComponent,
        AccountFilterComponent,
    ],
    templateUrl: '../handson-sheet.template.html',
    encapsulation: ViewEncapsulation.None // Global Styles
})
export class SeriesComponentSheetViewComponent implements OnInit {
    private static readonly PASTE_ROW_LIMIT = 1000;
    private static readonly PASTE_LOOKUP_BATCH_SIZE = 100;

    private readonly destroyRef = inject(DestroyRef);
    private readonly seriesQuery$ = new Subject<SeriesQuery>();
    @ViewChild('hot_anchor') hot_anchor: ElementRef;

    hot: HotInstance;
    @Input()
    process: any;
    @Input()
    components: WireComponent[];
    @Input()
    seriesComponents: SeriesComponent[];
    search: string;

    title: string;
    schema: any;
    column_list: any;

    series_light: any[];
    // Fast id → series lookup that mirrors series_light. Built incrementally as series
    // pages arrive (mergeSeriesIntoLookups + fetchMissingSeries) so per-row lookups
    // (isCalculationSeries) stay O(1) regardless of catalog size — important because
    // large pastes and save iterate every row.
    private readonly seriesById = new Map<string, any>();
    users: any[];

    component_filters: any[];
    component_lookups: HOTLookup;
    seriesLookups: HOTLookup;
    private pasteLookupToken = 0;
    private readonly pendingComponentPastes = new WeakMap<any, PendingPasteLookup>();
    private readonly pendingSeriesPastes = new WeakMap<any, PendingPasteLookup>();
    private readonly pasteResolvingValues = new Set<string>();
    private pasteInFlight = false;
    // Stash for a paste whose rows Handsontable hasn't created yet. HOT validates
    // asynchronously (validateCell wraps the validator in setTimeout), so when a paste
    // touches a validated column (Series) the rows are created in a later macrotask —
    // after afterPaste. We capture the paste here and resolve it from the CopyPaste.paste
    // afterChange, which fires once the rows exist. See getSeriesByName/pasteRowsExist.
    private pendingPasteContext: {
        pasted: any[][];
        config: { startRow: number; startCol: number; endRow: number; endCol: number };
    } | null = null;

    pageSize: number = 100;
    loading: boolean = false;

    @Output() refreshInner = new EventEmitter();

    constructor(private api: ApiService,
                private handsontableGenerator: HandsontableGeneratorService,
                private headerData: HeaderDataService,
                private notification: NotificationService) {
        this.hot = new HotInstance(["relationships.series.data.id"]);

        this.seriesQuery$.pipe(
            debounceTime(300),
            switchMap(({query, process, exactNameOnly, cachedNames}) => {
                const opts = new SearchQueryOptions(100, 1, 'name');
                opts.filters = exactNameOnly
                    ? [getBaseFilter(query, 'name')]
                    : query
                    ? [{
                        or: [
                            {name: 'name', op: 'ilike', val: `%${query}%`},
                            {name: 'description', op: 'ilike', val: `%${query}%`},
                        ]
                    }]
                    : [];
                return this.api.series_light.searchMany(opts).pipe(
                    map((response: ListResponse<SeriesLight>) => ({response, process, cachedNames: cachedNames ?? []})),
                    // Keep the outer subscription alive on HTTP errors — Model.searchMany
                    // rethrows via throwError, so without this a single 500/timeout would
                    // tear down seriesQuery$ for the rest of the session and HOT would
                    // silently never receive process() results again.
                    catchError(() => {
                        this.notification.openError('Series search failed. Please try again.');
                        process([]);
                        return EMPTY;
                    }),
                );
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(({response, process, cachedNames}) => {
            const names = this.mergeSeriesIntoLookups(response.data);
            process(this.uniqueNames([...(cachedNames ?? []), ...names]).slice(0, 100));
        });
    }

    ngOnInit(): void {
        this.headerData.title = 'Report Configuration';
        this.loadPage();
    }

    loadPage(): void {
        if (this.loading) return;
        this.loading = true;
        const observables = [];
        this.series_light = [];

        if (this.seriesComponents == null) {
            observables.push(this.loadNextSeriesComponents());
        } else {
            this.pageSize = 0;
            observables.push(firstValueFrom(this.fetchMissingSeries()));
        }

        if (this.components == null) {
            this.component_filters = [];
        } else {
            const filter: any = {
                name: 'id',
                op: 'in',
                val: this.components.map(
                    (component: WireComponent) => component.id
                )
            };
            this.component_filters = [filter];
        }
        // TODO Filter for resource types
        const component_options: SearchQueryOptions = new SearchQueryOptions();
        forkJoin(observables).pipe(concatMap(() => {
                const options: SearchQueryOptions = this.handsontableGenerator.getUserFilterForItems(this.seriesComponents);
                const users$: Observable<ListResponse<User>> = this.api.users.searchMany(options);

                if (this.seriesComponents == null) {
                    const filters: any = {
                        name: 'series_component',
                        op: 'ne',
                        val: null
                    };
                    component_options.filters = [filters];
                } else {
                    this.components = null;
                    const nestedFilter: any = {
                        name: 'id',
                        op: 'in',
                        val: this.seriesComponents.map((seriesComponents: SeriesComponent) => seriesComponents.id)
                    };
                    const parentFilters: any = {
                        name: 'series_components',
                        op: 'any',
                        val: nestedFilter
                    };
                    component_options.filters = [parentFilters];
                }
                return forkJoin([users$, this.api.component.searchMany(component_options)])
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        tap((response: [ListResponse<User>, ListResponse<WireComponent>]): void => {
                            this.users = response[0].data;
                            this.components = response[1].data;
                            this.createTable();
                        })
                    )
            }), takeUntilDestroyed(this.destroyRef),
            finalize(() => this.loading = false)
        ).subscribe();
        this.buildHeader();
    }

    loadNextSeriesComponents = (pageNumber: number = 0, pageSize?: number, filters?: object[]): Promise<SeriesComponent[]> => {
        // TODO Filter for resource types
        const options: SearchQueryOptions = new SearchQueryOptions();
        options.page_size = pageSize || this.pageSize;
        options.page_number = (pageNumber ?? 0) + 1;
        options.filters = filters;

        return firstValueFrom(this.api.series_component.searchMany(options).pipe(
            concatMap((response: ListResponse<SeriesComponent>) => {
                const incoming = response.data;
                this.seriesComponents = (this.seriesComponents || []).concat(incoming);
                // No need to mirror into a separate component-level array — HOT is the
                // source of truth post-init (settings.data is seeded from
                // seriesComponents in createTable, and HotInstance.query manages
                // subsequent pages via its own source_data). All reads downstream go
                // through hot.instance.getSourceData() / getSourceDataAtRow().
                return this.fetchMissingSeries().pipe(map(() => incoming));
            })));
    }

    fetchMissingSeries(): Observable<ListResponse<SeriesLight>> {
        if (!this.seriesComponents?.length) {
            return of(null);
        }
        const seriesComponentIds = this.seriesComponents.map(sc => sc.relationships.series.data.id);
        const seriesLightIds = new Set((this.series_light || []).map(s => s.id));

        const missingSeriesIds = seriesComponentIds.filter(id => !seriesLightIds.has(id));
        if (!missingSeriesIds?.length) {
            return of(null);
        }
        let options = new SearchQueryOptions();
        options.filters = [getBaseFilter(missingSeriesIds, 'id', 'in')];
        return this.api.series_light.searchMany(options).pipe(tap(result => {
            if (result?.data) {
                if (this.seriesLookups) {
                    this.mergeSeriesIntoLookups(result.data);
                } else {
                    // Pre-createTable path: seriesLookups doesn't exist yet, so
                    // mirror into series_light + seriesById directly. mergeSeriesIntoLookups
                    // will be the single update path once createTable runs.
                    this.series_light = [...this.series_light, ...result.data];
                    for (const s of result.data) {
                        this.seriesById.set(s.id, s);
                    }
                }
            }
        }));
    }

    private isCalculationSeries(rowData: any): boolean {
        const seriesId = rowData?.relationships?.series?.data?.id;
        if (!seriesId) {
            return false;
        }
        return this.seriesById.get(seriesId)?.attributes?.is_calculation === true;
    }

    /**
     * Custom data accessor for the Series autocomplete column. Mirrors the shape of
     * HandsontableGeneratorService.genLookupDataSource but additionally preserves
     * the raw pasted/typed text on the row when the lookup misses, so an unresolved
     * name stays visible in the cell (and the validator paints it red) instead of
     * the cell rendering blank.
     */
    private seriesDataAccessor = (row: any, value?: any): any => {
        if (row == null) {
            return null;
        }
        if (row.relationships == null) row.relationships = {};
        if (!row.relationships.series) row.relationships.series = {};

        if (value === undefined) {
            const id = row.relationships.series.data?.id;
            if (id) {
                return this.seriesLookups.idLookup[id] ?? row._unverifiedSeriesText ?? null;
            }
            return row._unverifiedSeriesText ?? null;
        }

        if (!row.relationships.series.data) {
            row.relationships.series.data = {};
        }
        const text = value == null ? '' : String(value);
        this.clearStalePendingPaste(this.pendingSeriesPastes, row, text);
        const matchedId = this.findSeriesIdForText(text);
        row.relationships.series.data.id = matchedId;
        row.relationships.series.data.type = 'series';
        if (matchedId || text === '') {
            delete row._unverifiedSeriesText;
        } else {
            row._unverifiedSeriesText = text;
        }
        return value;
    };

    private componentDataAccessor = (row: any, value?: any): any => {
        if (value !== undefined) {
            this.clearStalePendingPaste(this.pendingComponentPastes, row, value == null ? '' : String(value));
        }
        return this.handsontableGenerator.genLookupDataSource(this.component_lookups, 'component')(row, value);
    };

    createTable(): void {
        this.component_lookups = this.handsontableGenerator.gen_lookups(this.components);
        this.seriesLookups = this.handsontableGenerator.gen_lookups(this.series_light);
        this.schema = {
            id: null,
            type: 'series_component',
            attributes: {
                created_on: null,
                changed_on: null,
                report_group: null,
                can_edit: null,
                view_on_flowchart: null,
                series_order: null,
                latest_value: null
            },
            relationships: {
                series: {data: {id: null, type: 'series'}},
                component: {data: {id: null, type: 'component'}},
                created_by: {data: {id: null, type: 'users'}},
                changed_by: {data: {id: null, type: 'users'}}
            }
        };

        if (this.schema.relationships.process && this.process !== undefined) {
            this.schema.relationships.process.id = this.process.id;
        }

        this.column_list = [{
            data: 'attributes.report_group',
            title: 'Group',
        }, {
            data: 'attributes.series_order',
            title: 'Order',
            type: 'numeric',
        }, {
            data: 'attributes.can_edit',
            title: 'Edit',
            type: 'checkbox',
        }, {
            data: 'attributes.view_on_flowchart',
            title: 'FlowChart',
            type: 'checkbox',
        }, {
            data: this.seriesDataAccessor,
            title: 'Series',
            type: 'autocomplete',
            trimDropdown: false,
            allowInvalid: true,
            source: (query: string, process: (names: string[]) => void) => {
                const q = (query ?? '').trim();
                if (q && this.seriesLookups.valueLookup[q] !== undefined) {
                    process([q]);
                    return;
                }
                // Under 3 chars, serve cached display strings rather than hitting the
                // server — the /api_min/series_light endpoint runs a CollectionMixin
                // count() over() before pagination, and short ilike '%q%' searches
                // against name+description are expensive enough to dominate paste/QA
                // latency. Three chars is enough to make the contains query usefully
                // selective; shorter typing still gets local cache results immediately.
                if (q.length < 3) {
                    const cached = this.getCachedSeriesMatches(q).slice(0, 100);
                    if (!q) {
                        process(cached);
                        return;
                    }
                    this.seriesQuery$.next({
                        query: q,
                        process,
                        exactNameOnly: true,
                        cachedNames: cached,
                    });
                    return;
                }
                this.seriesQuery$.next({query: q, process});
            },
            validator: (value, callback) => {
                if (value == null || value === '') {
                    callback(true);
                    return;
                }
                // While a paste is mid-flight, treat the pasted text as valid so the
                // cell doesn't flash red between the local lookup miss and the server
                // resolve finishing.
                if (this.pasteResolvingValues.has(value)) {
                    callback(true);
                    return;
                }
                callback(this.findSeriesIdForText(String(value)) !== null);
            },
        }, {
            data: this.componentDataAccessor,
            title: 'Component',
            renderer: 'infinite_scroller',
        }, {
            data: 'attributes.created_by_name',
            readOnly: true,
            title: 'Created By',
        }, {
            data: 'attributes.changed_by_name',
            readOnly: true,
            title: 'Changed By',
        }, {
            data: 'attributes.changed_on',
            readOnly: true,
            title: 'Changed On',
            type: 'date',
            renderer: 'date_formatter'
        }, {
            data: 'attributes.created_on',
            readOnly: true,
            title: 'Created On',
            type: 'date',
            renderer: 'date_formatter'
        }];

        const extraAfterChange = (_changes: [number, string | number, any, any][], source: string) => {
            // A paste's overflow rows are only created when HOT applies the change
            // (applyChanges, which fires this afterChange) — and because validation is async
            // that can happen after afterPaste. If afterPaste deferred resolution because the
            // rows didn't exist yet, run it now that they do.
            if (source === 'CopyPaste.paste' && this.pendingPasteContext) {
                const {pasted, config} = this.pendingPasteContext;
                this.pendingPasteContext = null;
                this.getByName(pasted, config);
                this.getSeriesByName(pasted, config);
            }
            // While a paste is mid-flight, refreshTable would strip just-pasted rows
            // (data.id is still undefined until getSeriesByName.finalize resolves) and
            // shift the rows beneath, so the async repair would land on the wrong row.
            // The flag is cleared in finalize() once physical row indices are stable.
            // Note: enforcement of can_edit=false for calculation-series rows happens
            // in the `cells` callback below, which fires on every render — including
            // the one HOT runs immediately after this afterChange — so we don't need
            // to mutate it again here.
            if (!this.pasteInFlight) {
                this.refreshTable();
            }
        };

        this.hot = this.handsontableGenerator.generateTable(
            this.api.series_component,
            this.schema,
            this.column_list,
            this.hot,
            extraAfterChange,
            Boolean(this.pageSize)
        );

        this.setupInfiniteScroll();
        this.hot.ready = true;
        // Hand HOT its own array. From here on, HOT (and HotInstance.query() when
        // paginated) owns the data; reads go via hot.instance.getSourceData() /
        // getSourceDataAtRow(). Shallow copy keeps row objects shared with
        // seriesComponents (we never mutate those references), but isolates the
        // outer array from concat() calls in subsequent loadNextSeriesComponents.
        this.hot.settings.data = [...(this.seriesComponents || [])];
        this.hot.settings.rowHeaders = false;

        this.hot.settings.cells = (row, col) => {
            if (!this.hot.instance) {
                return;
            }
            const colName = this.hot.instance.getColHeader(col).toString();
            if (colName === 'Edit') {
                const rowData: any = this.hot.instance.getSourceDataAtRow(row);
                if (this.isCalculationSeries(rowData)) {
                    if (rowData.attributes.can_edit !== false) {
                        rowData.attributes.can_edit = false;
                    }
                    return {readOnly: true};
                }
            }
            // Returning undefined lets column-level cell properties apply unchanged
            // — important for readOnly on the audit columns (Created/Changed By/On),
            // because HOT's paste loop skips readOnly cells (handsontable.js:12219).
            return undefined;
        };
        this.hot.query_handler = this.loadNextSeriesComponents;
        this.hot.page_size = this.pageSize;

        if (this.pageSize) {
            const defaultLookup = ['name', 'description'];
            this.hot.query_lookup = {
                attributes: [],
                relationships: {
                    series: defaultLookup,
                    component: defaultLookup,
                    created_by: ['name'],
                    changed_by: ['name']
                }
            };
        }
        this.hot.initializeInstance(this.hot_anchor.nativeElement);
    }

    getByName(pasted, config: { startRow: number, startCol: number, endRow: number, endCol: number }) {
        const headers = this.hot.instance.getColHeader() as string[];
        const componentColIdx = headers.indexOf('Component');
        if (componentColIdx < config.startCol || componentColIdx > config.endCol) {
            return;
        }

        const token = this.nextPasteLookupToken();
        const pasteEntries: Array<{ rowData: any; text: string; token: number }> = [];
        const unknown = new Set<string>();
        pasted.forEach((row, pIdx) => {
            const value = row[componentColIdx - config.startCol];
            if (value == null || value === '') {
                return;
            }
            const text = String(value);
            const physicalRow = this.hot.instance.toPhysicalRow(config.startRow + pIdx);
            if (physicalRow == null) {
                return;
            }
            const rowData = this.hot.instance.getSourceDataAtRow(physicalRow);
            if (!rowData) {
                return;
            }
            if (!this.findComponentIdForText(text)) {
                this.pendingComponentPastes.set(rowData, {text, token});
                pasteEntries.push({rowData, text, token});
                this.getNameCandidates(text).forEach(candidate => unknown.add(candidate));
            }
        });

        if (unknown.size < 1) {
            return;
        }

        const options = new SearchQueryOptions();
        options.filters = [{
            or: Array.from(unknown).map(name => {
                return {name: 'name', op: 'ilike', val: '%' + name + '%'};
            })
        }];
        const ids: string[] = this.hot.change_ids;
        this.api.component.searchMany(options).pipe(first(), takeUntilDestroyed(this.destroyRef),
            tap(result => {
                this.components = _uniq(this.components.concat(result.data));
                this.component_lookups = this.handsontableGenerator.gen_lookups(this.components);
                pasteEntries.forEach(({rowData, text, token}) => {
                    if (!this.isCurrentPendingComponentPaste(rowData, text, token)) {
                        return;
                    }
                    if (!rowData.relationships) rowData.relationships = {};
                    if (!rowData.relationships.component) rowData.relationships.component = {};
                    rowData.relationships.component.data = {
                        id: this.findComponentIdForText(text),
                        type: 'component'
                    };
                    this.pendingComponentPastes.delete(rowData);
                });
                const col_index = this.hot.instance.getColHeader().indexOf('Component');
                this.hot.settings.columns[col_index].data = this.componentDataAccessor;
            })).subscribe(() => {
            this.hot.change_ids = ids;
            this.hot.instance.updateSettings(this.hot.settings, false);
        });
    }

    private getSeriesByName(pasted: any[][], config: {
        startRow: number,
        startCol: number,
        endRow: number,
        endCol: number
    }): void {
        const headers = this.hot.instance.getColHeader() as string[];
        const seriesColIdx = headers.indexOf('Series');
        if (seriesColIdx < config.startCol || seriesColIdx > config.endCol) {
            this.pasteInFlight = false;
            return;
        }

        // Capture the SeriesComponent row reference at paste time against HOT's live
        // source data. HotInstance.query() reloads HOT via loadData(deepCopy(source_data))
        // on every search/pagination, so the only array that's always in sync with
        // what HOT is rendering is hot.instance's own source data. getSourceDataAtRow
        // returns the live row object regardless of sort/filter/search state, so the
        // stashed reference stays correct when finalisePaste runs.
        const token = this.nextPasteLookupToken();
        const paste_entries: Array<{ rowData: any; text: string; token: number }> = [];
        const unknown = new Set<string>();
        pasted.forEach((row, pIdx) => {
            const value = row[seriesColIdx - config.startCol];
            if (value == null || value === '') {
                return;
            }
            const text = String(value);
            const physicalRow = this.hot.instance.toPhysicalRow(config.startRow + pIdx);
            if (physicalRow == null) return;
            const rowData = this.hot.instance.getSourceDataAtRow(physicalRow);
            if (!rowData) return;
            this.pendingSeriesPastes.set(rowData, {text, token});
            paste_entries.push({rowData, text, token});
            if (!this.findSeriesIdForText(text)) {
                this.getNameCandidates(text).forEach(candidate => unknown.add(candidate));
            }
        });

        if (paste_entries.length === 0) {
            this.pasteInFlight = false;
            return;
        }

        const finalisePaste = (): void => {
            const notFound: string[] = [];
            paste_entries.forEach(({rowData, text, token}) => {
                if (!this.isCurrentPendingSeriesPaste(rowData, text, token)) {
                    return;
                }
                if (!rowData.relationships) rowData.relationships = {};
                if (!rowData.relationships.series) rowData.relationships.series = {};
                const id = this.findSeriesIdForText(text);
                rowData.relationships.series.data = {id, type: 'series'};
                if (id) {
                    delete rowData._unverifiedSeriesText;
                    // Normalize can_edit for calculation-series rows here rather than
                    // relying on the `cells` callback. With renderAllRows: false a
                    // large paste can leave most rows unrendered until after save,
                    // and the cells-callback mutation would be skipped entirely.
                    if (this.isCalculationSeries(rowData)) {
                        if (!rowData.attributes) {
                            rowData.attributes = {};
                        }
                        rowData.attributes.can_edit = false;
                    }
                } else {
                    // Keep the pasted text on the row so the cell renders the unresolved
                    // name (in red, via the Series validator) rather than going blank.
                    rowData._unverifiedSeriesText = text;
                    notFound.push(text);
                }
                this.pendingSeriesPastes.delete(rowData);
            });
            // Release the "paste in flight" pass for these values before re-rendering, so
            // HOT re-validates against the now-merged seriesLookups (resolved → normal,
            // genuinely missing → red).
            paste_entries.forEach(({text}) => this.pasteResolvingValues.delete(text));
            // Physical row indices are now stable — drop the paste-in-flight guard
            // before re-rendering so any downstream caller can run normally.
            this.pasteInFlight = false;
            // Scope re-validation to the Series column. Validating every cell here
            // would run the built-in date validator on existing rows' Changed On /
            // Created On values; the backend returns ISO date strings which fail
            // HOT's default DD/MM/YYYY format check (correctFormat is off) and the
            // cells would get htInvalid even though the data is fine.
            this.hot.instance.validateColumns([seriesColIdx], () => this.hot.instance.render());
            if (notFound.length === 1) {
                this.notification.openError(`Series "${notFound[0]}" was not found`);
            } else if (notFound.length > 1) {
                this.notification.openError('Some series were not found');
            }
        };

        if (unknown.size === 0) {
            finalisePaste();
            return;
        }

        const requests = this.chunk(Array.from(unknown), SeriesComponentSheetViewComponent.PASTE_LOOKUP_BATCH_SIZE)
            .map(names => {
                const opts = new SearchQueryOptions(names.length, 1, 'name');
                opts.filters = [getBaseFilter(names, 'name', 'in')];
                return this.api.series_light.searchMany(opts).pipe(first());
            });

        forkJoin(requests).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (responses: ListResponse<SeriesLight>[]) => {
                responses.forEach(response => this.mergeSeriesIntoLookups(response.data));
                finalisePaste();
            },
            error: () => {
                // Still run finalize so paste_map rows get their {id: null} treatment
                // and pasteInFlight is cleared — leaving the flag stuck would freeze
                // refreshTable for the rest of the session.
                finalisePaste();
            },
        });
    }

    refreshTable = (): void => {
        if (!this.hot.instance) {
            return;
        }
        // While a paste is resolving, removing rows would shift physical indices and
        // the async repair in getSeriesByName.finalisePaste() would land on the wrong
        // row. The flag is cleared in finalisePaste once indices are stable.
        if (this.pasteInFlight) {
            return;
        }
        const sourceData = this.hot.instance.getSourceData();
        const visualRowsToRemove: number[] = [];
        for (let i = sourceData.length - 1; i >= 0; i--) {
            const item: any = sourceData[i];
            const hasId = item?.relationships?.series?.data?.id;
            if (!hasId && !item?._unverifiedSeriesText && !this.hasReportConfigRowData(item)) {
                const visualRow = this.hot.instance.toVisualRow(i);
                if (visualRow != null && visualRow >= 0) {
                    visualRowsToRemove.push(visualRow);
                }
            }
        }
        visualRowsToRemove.sort((a, b) => b - a).forEach(row => {
            this.hot.instance.alter('remove_row', row, 1);
        });
        // HOT's minSpareRows: 3 (handsontable-generator.service.ts) already keeps
        // empty rows at the bottom for the user to fill in — no manual insert needed.
        this.hot.instance.render();
    }

    /**
     * Whether every row this paste targets already exists in HOT's source data. Used to
     * decide if afterPaste can resolve immediately or must defer to afterChange: a paste
     * that overflows the existing rows has its extra rows created by HOT's (async)
     * applyChanges, which runs after afterPaste, so toPhysicalRow/getSourceDataAtRow
     * return null for those rows until then.
     */
    private pasteRowsExist(pasted: any[][], cfg: { startRow: number }): boolean {
        if (!pasted?.length) {
            return false;
        }
        const physical = this.hot.instance.toPhysicalRow(cfg.startRow + pasted.length - 1);
        return physical != null && this.hot.instance.getSourceDataAtRow(physical) != null;
    }

    setupInfiniteScroll(): void {
        this.hot.settings.beforePaste = (pasted: any[][], config: any[]) => {
            const cfg = config?.[0];
            if (!cfg) return;
            const headers = this.hot.instance.getColHeader() as string[];
            const seriesColIdx = headers.indexOf('Series');
            // Non-Series paste (Group/Order/Edit/FlowChart/Component) — no async
            // Series resolution is triggered, so the row cap doesn't apply and
            // pasteInFlight must stay false. getSeriesByName has its own bounds
            // check and will short-circuit without touching anything.
            if (seriesColIdx < cfg.startCol || seriesColIdx > cfg.endCol) {
                return;
            }
            // Hard cap on Series-bearing pastes only: returning false cancels the
            // paste entirely — no rows are written and afterPaste never fires, so
            // no pasteInFlight cleanup is required for the cancelled call.
            if (pasted.length > SeriesComponentSheetViewComponent.PASTE_ROW_LIMIT) {
                this.notification.openError(
                    `Paste exceeds the ${SeriesComponentSheetViewComponent.PASTE_ROW_LIMIT}-row limit. ` +
                    `Please trim your selection and try again.`
                );
                return false;
            }
            const relCol = seriesColIdx - cfg.startCol;
            pasted.forEach(row => {
                const v = row[relCol];
                if (v != null && v !== '') {
                    this.pasteResolvingValues.add(String(v));
                }
            });
            this.pasteInFlight = true;
        };
        this.hot.settings.afterPaste = (pasted, config) => {
            const cfg = config?.[0];
            if (!cfg) {
                return;
            }
            // HOT validation is always async, so a paste touching a validated column (Series)
            // is applied — and its overflow rows created — in a later macrotask, after this
            // hook. Resolve now only if every target row already exists (the paste fit the
            // current rows, or it had no validated column so applyChanges ran synchronously);
            // otherwise defer to the CopyPaste.paste afterChange, which fires once the rows
            // are created.
            if (this.pasteRowsExist(pasted, cfg)) {
                this.getByName(pasted, cfg);
                this.getSeriesByName(pasted, cfg);
            } else {
                this.pendingPasteContext = {pasted, config: cfg};
            }
        };

        this.hot.settings.afterOnCellMouseDown = (event, coords) => {
            const overlayHandle = this.handsontableGenerator.genScrollerCallback(event, coords, this.hot, this.hot.instance.getSourceData(), this.component_lookups,
                this.components, 'component', 'component', this.component_filters, null);
        };
    }

    save = (): void => {
        if (this.hot.replacementLoading) {
            this.notification.openError('Cannot save while report configuration data is loading. Please try again.');
            return;
        }
        // Iterate HOT's source data (what hot.save() reads via getSourceData) so
        // we cover every row regardless of render state. The cells callback only
        // mutates can_edit when the Edit cell is rendered, and renderAllRows: false
        // means a large paste can leave most calc-series rows unrendered until
        // after save — this is the catch-all that guarantees we don't ship
        // can_edit=true to a calculation series.
        const sourceData = this.hot.instance.getSourceData();
        const invalidRows = this.getRowsWithMissingSeries(sourceData);
        if (invalidRows.length) {
            const rows = invalidRows.map(row => row + 1).join(', ');
            this.notification.openError(`Cannot save. Series missing or unresolved at row(s): ${rows}`);
            const seriesColIdx = (this.hot.instance.getColHeader() as string[]).indexOf('Series');
            if (seriesColIdx >= 0) {
                this.hot.instance.validateColumns([seriesColIdx], () => this.hot.instance.render());
            }
            return;
        }
        sourceData.forEach((sc: any) => {
            if (sc?.attributes?.series_order == "") {
                sc.attributes.series_order = null;
            }
            if (this.isCalculationSeries(sc)) {
                if (!sc.attributes) {
                    sc.attributes = {};
                }
                sc.attributes.can_edit = false;
            }
        });
        this.hot.save();
        this.refreshTable();
    }

    download = (): void => {
        this.hot.download();
    }

    refresh = (): void => {
        if (this.process) {
            this.refreshInner.emit();
        } else {
            this.seriesComponents = null;
            this.components = null;
            this.loadPage();
        }
    }

    buildHeader(): void {
        if (this.process) {
            this.headerData.title = 'Report Configuration: ' + this.process.attributes.name;
            this.headerData.setPath(this.process);
        } else {
            this.headerData.title = 'Report Configuration';
        }
        this.headerData.buttons = [
            {name: 'Save', func: this.save, class: 'icon-save', params: {}},
            {name: 'Download', func: this.download, class: 'icon-download', params: {}},
            {name: 'Refresh', func: this.refresh, class: 'icon-refresh', params: {}}
        ];
    }

    private mergeSeriesIntoLookups(series: SeriesLight[]): string[] {
        const names: string[] = [];
        for (const s of series) {
            const display = this.getSeriesDisplay(s);
            if (!display) {
                continue;
            }
            if (!(display in this.seriesLookups.valueLookup)) {
                this.seriesLookups.source.push(display);
            }
            this.seriesLookups.valueLookup[display] = s.id;
            this.seriesLookups.idLookup[s.id] = display;
            const name = s.attributes?.name?.trim() ?? '';
            this.seriesLookups.nameLookup[display] = name;
            this.seriesById.set(s.id, s);
            names.push(display);
        }
        const existingIds = new Set((this.series_light || []).map(sl => sl.id));
        this.series_light = [...(this.series_light || []), ...series.filter(s => !existingIds.has(s.id))];
        return names;
    }

    private findComponentIdForText(text: string): string | null {
        const trimmed = text.trim();
        const exactDisplayId = this.component_lookups.valueLookup[text] ?? this.component_lookups.valueLookup[trimmed];
        if (exactDisplayId) {
            return exactDisplayId;
        }
        const matchingDisplays = Object.keys(this.component_lookups.nameLookup)
            .filter(display => this.component_lookups.nameLookup[display]?.trim() === trimmed);
        return matchingDisplays.length === 1
            ? this.component_lookups.valueLookup[matchingDisplays[0]]
            : null;
    }

    private findSeriesIdForText(text: string): string | null {
        const trimmed = text.trim();
        const exactDisplayId = this.seriesLookups.valueLookup[text] ?? this.seriesLookups.valueLookup[trimmed];
        if (exactDisplayId) {
            return exactDisplayId;
        }
        const byName = (this.series_light || []).find(s => s?.attributes?.name?.trim() === trimmed);
        return byName?.id ?? null;
    }

    private getCachedSeriesMatches(query: string): string[] {
        const lower = query.toLowerCase();
        return query
            ? this.seriesLookups.source.filter(s => s && s.toLowerCase().includes(lower))
            : this.seriesLookups.source;
    }

    private uniqueNames(names: string[]): string[] {
        return Array.from(new Set(names.filter(name => name)));
    }

    private nextPasteLookupToken(): number {
        this.pasteLookupToken += 1;
        return this.pasteLookupToken;
    }

    private clearStalePendingPaste(pendingPastes: WeakMap<any, PendingPasteLookup>, row: any, text: string): void {
        if (!row) {
            return;
        }
        const pending = pendingPastes.get(row);
        if (pending && pending.text !== text) {
            pendingPastes.delete(row);
        }
    }

    private isCurrentPendingPaste(
        pendingPastes: WeakMap<any, PendingPasteLookup>,
        rowData: any,
        text: string,
        token: number,
    ): boolean {
        const pending = pendingPastes.get(rowData);
        return pending?.token === token
            && pending.text === text
            && Boolean(this.hot.instance?.getSourceData().includes(rowData));
    }

    private isCurrentPendingComponentPaste(rowData: any, text: string, token: number): boolean {
        if (!this.isCurrentPendingPaste(this.pendingComponentPastes, rowData, text, token)) {
            return false;
        }
        const currentId = rowData?.relationships?.component?.data?.id;
        return currentId == null || currentId === this.findComponentIdForText(text);
    }

    private isCurrentPendingSeriesPaste(rowData: any, text: string, token: number): boolean {
        if (!this.isCurrentPendingPaste(this.pendingSeriesPastes, rowData, text, token)) {
            return false;
        }
        if (rowData?._unverifiedSeriesText) {
            return rowData._unverifiedSeriesText === text;
        }
        const currentId = rowData?.relationships?.series?.data?.id;
        return Boolean(currentId) && currentId === this.findSeriesIdForText(text);
    }

    private getSeriesDisplay(series: SeriesLight): string {
        const description = series.attributes?.description?.trim();
        const name = series.attributes?.name?.trim() ?? '';
        return description ? `${name}-${description}` : name;
    }

    private getNameCandidates(text: string): string[] {
        const trimmed = text.trim();
        if (!trimmed) {
            return [];
        }
        const candidates = new Set<string>([trimmed]);
        const parts = trimmed.split('-');
        for (let i = 1; i < parts.length; i++) {
            const candidate = parts.slice(0, i).join('-').trim();
            if (candidate) {
                candidates.add(candidate);
            }
        }
        return Array.from(candidates);
    }

    private getRowsWithMissingSeries(sourceData: any[]): number[] {
        const invalidRows: number[] = [];
        sourceData.forEach((row, index) => {
            if (!this.hasReportConfigRowData(row)) {
                return;
            }
            if (row?._unverifiedSeriesText || !row?.relationships?.series?.data?.id) {
                const visualRow = this.hot.instance.toVisualRow(index);
                invalidRows.push(visualRow != null && visualRow >= 0 ? visualRow : index);
            }
        });
        return invalidRows;
    }

    private hasReportConfigRowData(row: any): boolean {
        const attributes = row?.attributes ?? {};
        return this.hasCellValue(row?.id)
            || this.hasCellValue(row?._unverifiedSeriesText)
            || this.hasCellValue(row?.relationships?.series?.data?.id)
            || this.hasCellValue(row?.relationships?.component?.data?.id)
            || this.hasCellValue(attributes.report_group)
            || this.hasCellValue(attributes.series_order)
            || this.hasCellValue(attributes.can_edit)
            || this.hasCellValue(attributes.view_on_flowchart);
    }

    private hasCellValue(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    private chunk<T>(items: T[], size: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < items.length; i += size) {
            chunks.push(items.slice(i, i + size));
        }
        return chunks;
    }

}
